import React, { useState, useEffect } from 'react';
import {
  UsersIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon,
  ShoppingCartIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from 'react-hot-toast';

function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [allCookies, setAllCookies] = useState([]);
  const [filters, setFilters] = useState({ role: '', search: '' });
  const [wallets, setWallets] = useState([]);

  useEffect(() => {
    loadUsers();
    loadPendingUsers();
    loadStats();
    loadLogs();
    loadAllCookies();
    loadWallets();
  }, [filters]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.users);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    }
    setLoading(false);
  };

  const loadWallets = async () => {
    try {
      const response = await api.get('/crypto/admin/wallets');
      if (response.data.success) setWallets(response.data.wallets);
    } catch (error) {
      console.error('Error loading wallets:', error);
    }
  };

  const loadPendingUsers = async () => {
    try {
      const response = await api.get('/admin/pending');
      setPendingUsers(response.data.users);
    } catch (error) {}
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data.stats);
    } catch (error) {}
  };

  const loadLogs = async () => {
    try {
      const response = await api.get('/admin/logs');
      setLogs(response.data.logs);
    } catch (error) {}
  };

  const loadAllCookies = async () => {
    try {
      const response = await api.get('/cookies?all=true');
      setAllCookies(response.data.cookies);
    } catch (error) {
      console.error('Error cargando cookies:', error);
    }
  };

  const handleApproveUser = async (userId) => {
    const permissions = {
      canCreateCookies: true,
      canMakePayments: true,
      canEditCookies: true,
      canDeleteCookies: false,
      canViewAllCookies: false,
      maxCookies: 5,
      maxCardsPerCookie: 3,
      maxDailyCookies: 3,
      smsLimit: 10,
      canUseSms: true,
      walletLimit: 500,
      canUseWallet: true,
      canWithdraw: false,
      withdrawLimit: 50
    };
    
    try {
      await api.post(`/admin/users/${userId}/approve`, { permissions });
      toast.success('Usuario aprobado con permisos básicos');
      loadPendingUsers();
      loadUsers();
      loadLogs();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al aprobar usuario');
    }
  };

  const handleRejectUser = async (userId) => {
    const reason = prompt('Motivo del rechazo:', '');
    if (!reason) return;
    try {
      await api.post(`/admin/users/${userId}/reject`, { reason });
      toast.success('Usuario rechazado');
      loadPendingUsers();
      loadLogs();
    } catch (error) {
      toast.error('Error al rechazar usuario');
    }
  };

  // 🔥 CORREGIDO: Enviar TODOS los permisos correctamente
  const handleUpdateUser = async (userId, updates) => {
    try {
      console.log('📤 Enviando actualización:', JSON.stringify(updates, null, 2));
      const response = await api.put(`/admin/users/${userId}`, updates);
      console.log('✅ Respuesta:', response.data);
      toast.success('Usuario actualizado correctamente');
      loadUsers();
      loadLogs();
      setShowEditModal(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.error || 'Error al actualizar usuario');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Eliminar este usuario permanentemente?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('Usuario eliminado');
      loadUsers();
      loadLogs();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  const handleAddWalletBalance = async (userId, amount, description) => {
    try {
      const response = await api.post('/crypto/admin/add-balance', {
        userId, amount: parseFloat(amount), currency: 'USDT', description
      });
      if (response.data.success) {
        toast.success(`Añadido $${amount} al usuario`);
        loadWallets();
        loadUsers();
      }
    } catch (error) {
      toast.error('Error al añadir saldo');
    }
  };

  const handleRemoveWalletBalance = async (userId, amount, description) => {
    try {
      const response = await api.post('/crypto/admin/remove-balance', {
        userId, amount: parseFloat(amount), currency: 'USDT', description
      });
      if (response.data.success) {
        toast.success(`Removido $${amount} del usuario`);
        loadWallets();
        loadUsers();
      }
    } catch (error) {
      toast.error('Error al remover saldo');
    }
  };

  const openEditModal = (user) => {
    const perms = user.permissions || {};
    setEditingUser({
      ...user,
      editRole: user.role,
      editPermissions: {
        canCreateCookies: perms.canCreateCookies || false,
        canMakePayments: perms.canMakePayments || false,
        canEditCookies: perms.canEditCookies || false,
        canDeleteCookies: perms.canDeleteCookies || false,
        canViewAllCookies: perms.canViewAllCookies || false,
        maxCookies: perms.maxCookies || 5,
        maxCardsPerCookie: perms.maxCardsPerCookie || 3,
        maxDailyCookies: perms.maxDailyCookies || 3,
        canUseSms: perms.canUseSms || false,
        smsLimit: perms.smsLimit || 10,
        canUseWallet: perms.canUseWallet || false,
        canWithdraw: perms.canWithdraw || false,
        walletLimit: perms.walletLimit || 500,
        withdrawLimit: perms.withdrawLimit || 50,
      }
    });
    setShowEditModal(true);
  };

  // 🔥 CORREGIDO: Guardar TODOS los permisos
  const handleSavePermissions = () => {
    if (!editingUser) return;
    
    const updates = {
      role: editingUser.editRole,
      permissions: {
        canCreateCookies: editingUser.editPermissions?.canCreateCookies || false,
        canMakePayments: editingUser.editPermissions?.canMakePayments || false,
        canEditCookies: editingUser.editPermissions?.canEditCookies || false,
        canDeleteCookies: editingUser.editPermissions?.canDeleteCookies || false,
        canViewAllCookies: editingUser.editPermissions?.canViewAllCookies || false,
        maxCookies: parseInt(editingUser.editPermissions?.maxCookies) || 5,
        maxCardsPerCookie: parseInt(editingUser.editPermissions?.maxCardsPerCookie) || 3,
        maxDailyCookies: parseInt(editingUser.editPermissions?.maxDailyCookies) || 3,
        canUseSms: editingUser.editPermissions?.canUseSms || false,
        smsLimit: parseInt(editingUser.editPermissions?.smsLimit) || 10,
        canUseWallet: editingUser.editPermissions?.canUseWallet || false,
        canWithdraw: editingUser.editPermissions?.canWithdraw || false,
        walletLimit: parseInt(editingUser.editPermissions?.walletLimit) || 500,
        withdrawLimit: parseInt(editingUser.editPermissions?.withdrawLimit) || 50,
      }
    };
    
    handleUpdateUser(editingUser._id, updates);
  };

  // Actualizar un permiso específico
  const updateEditPermission = (key, value) => {
    setEditingUser(prev => ({
      ...prev,
      editPermissions: {
        ...prev.editPermissions,
        [key]: value
      }
    }));
  };

  const getRoleBadge = (role) => {
    const colors = {
      owner: { bg: '#ff00ff20', color: '#ff00ff', border: '#ff00ff' },
      admin: { bg: '#00ffff20', color: '#00ffff', border: '#00ffff' },
      user: { bg: '#ccff0020', color: '#ccff00', border: '#ccff00' },
      pending: { bg: '#ff660020', color: '#ff6600', border: '#ff6600' }
    };
    return colors[role] || colors.user;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#000000' }}>
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#000000', padding: '1rem', paddingBottom: '6rem', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem', padding: '1rem 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <ShieldCheckIcon style={{ width: '2rem', height: '2rem', color: '#ff00ff' }} />
            <h1 style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 'bold' }}>Panel de Administración</h1>
          </div>
          <span style={{ background: '#ff00ff20', color: '#ff00ff', padding: '0.5rem 1rem', borderRadius: '2rem', border: '1px solid #ff00ff', fontSize: '0.8rem' }}>👑 Owner Mode</span>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <div style={{ background: '#111', border: '1px solid #222', borderRadius: '1rem', padding: '1rem', textAlign: 'center' }}>
            <div style={{ color: '#00ffff', fontSize: '1.5rem', fontWeight: 'bold' }}>{stats.total}</div>
            <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Total Usuarios</div>
          </div>
          <div style={{ background: '#111', border: '1px solid #222', borderRadius: '1rem', padding: '1rem', textAlign: 'center' }}>
            <div style={{ color: '#ccff00', fontSize: '1.5rem', fontWeight: 'bold' }}>{stats.active || 0}</div>
            <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Activos</div>
          </div>
          <div style={{ background: '#111', border: '1px solid #222', borderRadius: '1rem', padding: '1rem', textAlign: 'center' }}>
            <div style={{ color: '#ff6600', fontSize: '1.5rem', fontWeight: 'bold' }}>{stats.pending || 0}</div>
            <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Pendientes</div>
          </div>
          <div style={{ background: '#111', border: '1px solid #222', borderRadius: '1rem', padding: '1rem', textAlign: 'center' }}>
            <div style={{ color: '#ff00ff', fontSize: '1.5rem', fontWeight: 'bold' }}>{stats.byRole?.find(r => r._id === 'admin')?.count || 0}</div>
            <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Admins</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #222', flexWrap: 'wrap' }}>
        {[
          { id: 'pending', label: `Pendientes (${pendingUsers.length})`, icon: ClockIcon },
          { id: 'users', label: 'Usuarios', icon: UsersIcon },
          { id: 'cookies', label: `Cookies (${allCookies.length})`, icon: ShoppingCartIcon },
          { id: 'logs', label: 'Logs', icon: ChartBarIcon }
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1rem',
            background: activeTab === tab.id ? '#00ffff15' : 'transparent',
            border: 'none', borderBottom: activeTab === tab.id ? '2px solid #00ffff' : '2px solid transparent',
            color: activeTab === tab.id ? '#00ffff' : '#9ca3af', cursor: 'pointer', fontSize: '0.8rem'
          }}>
            <tab.icon style={{ width: '1rem', height: '1rem' }} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Pendientes */}
      {activeTab === 'pending' && (
        <div>
          {pendingUsers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>No hay usuarios pendientes</div>
          ) : (
            pendingUsers.map(user => (
              <div key={user._id} style={{ background: '#111', border: '1px solid #ff6600', borderRadius: '1rem', padding: '1rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ color: '#fff', fontWeight: 'bold' }}>{user.name}</span>
                      <span style={{ background: '#ff660020', color: '#ff6600', padding: '0.2rem 0.6rem', borderRadius: '1rem', fontSize: '0.7rem' }}>Pendiente</span>
                    </div>
                    <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>@{user.username} • {user.email}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => handleApproveUser(user._id)} style={{ padding: '0.5rem 1rem', background: '#ccff00', border: 'none', borderRadius: '0.5rem', color: '#000', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem' }}>✅ Aprobar</button>
                    <button onClick={() => handleRejectUser(user._id)} style={{ padding: '0.5rem 1rem', background: '#ff0033', border: 'none', borderRadius: '0.5rem', color: '#fff', cursor: 'pointer', fontSize: '0.8rem' }}>❌ Rechazar</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Usuarios */}
      {activeTab === 'users' && (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {users.filter(u => u.role !== 'owner').map(user => {
            const roleColor = getRoleBadge(user.role);
            return (
              <div key={user._id} style={{ background: '#111', border: `1px solid ${roleColor.border}`, borderRadius: '1rem', padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                      <span style={{ color: '#fff', fontWeight: 'bold' }}>{user.name}</span>
                      <span style={{ background: roleColor.bg, color: roleColor.color, padding: '0.2rem 0.6rem', borderRadius: '1rem', fontSize: '0.7rem', border: `1px solid ${roleColor.color}` }}>{user.role}</span>
                      {user.status === 'active' && <span style={{ color: '#ccff00', fontSize: '0.7rem' }}>✓ Activo</span>}
                    </div>
                    <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>@{user.username} • {user.email}</div>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.5rem', fontSize: '0.7rem' }}>
                      <span style={{ background: '#1a1a1a', padding: '0.2rem 0.5rem', borderRadius: '0.5rem' }}>
                        🍪 {user.counters?.totalCookies || 0}/{user.permissions?.maxCookies || 5}
                      </span>
                      <span style={{ background: '#1a1a1a', padding: '0.2rem 0.5rem', borderRadius: '0.5rem' }}>
                        📱 SMS: {user.permissions?.canUseSms ? '✅' : '❌'}
                      </span>
                      <span style={{ background: '#1a1a1a', padding: '0.2rem 0.5rem', borderRadius: '0.5rem' }}>
                        💰 ${user.walletBalance || 0}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => openEditModal(user)} style={{ background: 'none', border: '1px solid #00ffff', borderRadius: '0.5rem', padding: '0.4rem', color: '#00ffff', cursor: 'pointer' }}>
                      <PencilIcon style={{ width: '1.1rem' }} />
                    </button>
                    <button onClick={() => handleDeleteUser(user._id)} style={{ background: 'none', border: '1px solid #ff0033', borderRadius: '0.5rem', padding: '0.4rem', color: '#ff0033', cursor: 'pointer' }}>
                      <TrashIcon style={{ width: '1.1rem' }} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Cookies Tab */}
      {activeTab === 'cookies' && (
        <div>
          {allCookies.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>No hay cookies</div>
          ) : (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {allCookies.slice(0, 20).map(cookie => (
                <div key={cookie._id} style={{ background: '#111', border: '1px solid #222', borderRadius: '1rem', padding: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ color: '#fff', fontWeight: 'bold' }}>{cookie.name || 'Cookie'}</span>
                    <span style={{ color: '#9ca3af', fontSize: '0.75rem', marginLeft: '0.5rem' }}>${cookie.amount}</span>
                  </div>
                  <span style={{ 
                    background: cookie.status === 'activa' ? '#ccff0020' : '#ff003320',
                    color: cookie.status === 'activa' ? '#ccff00' : '#ff0033',
                    padding: '0.2rem 0.5rem', borderRadius: '1rem', fontSize: '0.7rem'
                  }}>
                    {cookie.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal de Edición */}
      {showEditModal && editingUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#111', border: '1px solid #00ffff', borderRadius: '1.5rem', maxWidth: '650px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: '1.25rem', borderBottom: '1px solid #222', position: 'sticky', top: 0, background: '#111', zIndex: 1 }}>
              <h3 style={{ color: '#fff', fontSize: '1.1rem', margin: 0 }}>Editar: {editingUser.name}</h3>
              <p style={{ color: '#9ca3af', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>@{editingUser.username}</p>
            </div>

            <div style={{ padding: '1.25rem' }}>
              {/* Rol */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ color: '#00ffff', display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 'bold' }}>Rol</label>
                <select value={editingUser.editRole} onChange={(e) => setEditingUser({ ...editingUser, editRole: e.target.value })}
                  style={{ width: '100%', padding: '0.6rem', background: '#1a1a1a', border: '1px solid #333', borderRadius: '0.5rem', color: '#fff', fontSize: '0.9rem' }}>
                  <option value="user">Usuario</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>

              {/* Permisos de Cookies */}
              <div style={{ marginBottom: '1.25rem', padding: '1rem', background: '#1a1a1a', borderRadius: '1rem' }}>
                <h4 style={{ color: '#ff9900', margin: '0 0 0.75rem', fontSize: '0.9rem' }}>🍪 Cookies</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  {[
                    { key: 'canCreateCookies', label: 'Crear Cookies' },
                    { key: 'canMakePayments', label: 'Hacer Pagos' },
                    { key: 'canEditCookies', label: 'Editar Cookies' },
                    { key: 'canDeleteCookies', label: 'Eliminar Cookies' },
                    { key: 'canViewAllCookies', label: 'Ver Todas' },
                  ].map(perm => (
                    <label key={perm.key} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#ddd', cursor: 'pointer' }}>
                      <input type="checkbox" checked={editingUser.editPermissions?.[perm.key] || false}
                        onChange={(e) => updateEditPermission(perm.key, e.target.checked)} />
                      {perm.label}
                    </label>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                  {[
                    { key: 'maxCookies', label: 'Máx Cookies' },
                    { key: 'maxCardsPerCookie', label: 'Máx Tarjetas/Cookie' },
                    { key: 'maxDailyCookies', label: 'Máx Diarias' },
                  ].map(perm => (
                    <div key={perm.key}>
                      <label style={{ fontSize: '0.65rem', color: '#9ca3af', display: 'block', marginBottom: '0.2rem' }}>{perm.label}</label>
                      <input type="number" value={editingUser.editPermissions?.[perm.key] || 0}
                        onChange={(e) => updateEditPermission(perm.key, parseInt(e.target.value) || 0)}
                        style={{ width: '100%', padding: '0.4rem', background: '#0a0a0a', border: '1px solid #333', borderRadius: '0.4rem', color: '#fff', fontSize: '0.85rem' }} />
                    </div>
                  ))}
                </div>
              </div>

              {/* SMS */}
              <div style={{ marginBottom: '1.25rem', padding: '1rem', background: '#1a1a1a', borderRadius: '1rem' }}>
                <h4 style={{ color: '#00ffff', margin: '0 0 0.75rem', fontSize: '0.9rem' }}>📱 SMS</h4>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontSize: '0.8rem', color: '#ddd', cursor: 'pointer' }}>
                  <input type="checkbox" checked={editingUser.editPermissions?.canUseSms || false}
                    onChange={(e) => updateEditPermission('canUseSms', e.target.checked)} />
                  Habilitar SMS
                </label>
                <div>
                  <label style={{ fontSize: '0.65rem', color: '#9ca3af' }}>Límite diario</label>
                  <input type="number" value={editingUser.editPermissions?.smsLimit || 10}
                    onChange={(e) => updateEditPermission('smsLimit', parseInt(e.target.value) || 10)}
                    style={{ width: '100%', padding: '0.4rem', background: '#0a0a0a', border: '1px solid #333', borderRadius: '0.4rem', color: '#fff', fontSize: '0.85rem', marginTop: '0.2rem' }} />
                </div>
              </div>

              {/* Wallet */}
              <div style={{ marginBottom: '1.25rem', padding: '1rem', background: '#1a1a1a', borderRadius: '1rem' }}>
                <h4 style={{ color: '#ccff00', margin: '0 0 0.75rem', fontSize: '0.9rem' }}>💰 Wallet</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#ddd', cursor: 'pointer' }}>
                    <input type="checkbox" checked={editingUser.editPermissions?.canUseWallet || false}
                      onChange={(e) => updateEditPermission('canUseWallet', e.target.checked)} />
                    Habilitar Wallet
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#ddd', cursor: 'pointer' }}>
                    <input type="checkbox" checked={editingUser.editPermissions?.canWithdraw || false}
                      onChange={(e) => updateEditPermission('canWithdraw', e.target.checked)} />
                    Permitir Retiros
                  </label>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <div>
                    <label style={{ fontSize: '0.65rem', color: '#9ca3af' }}>Límite saldo</label>
                    <input type="number" value={editingUser.editPermissions?.walletLimit || 500}
                      onChange={(e) => updateEditPermission('walletLimit', parseInt(e.target.value) || 500)}
                      style={{ width: '100%', padding: '0.4rem', background: '#0a0a0a', border: '1px solid #333', borderRadius: '0.4rem', color: '#fff', fontSize: '0.85rem' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.65rem', color: '#9ca3af' }}>Límite retiro</label>
                    <input type="number" value={editingUser.editPermissions?.withdrawLimit || 50}
                      onChange={(e) => updateEditPermission('withdrawLimit', parseInt(e.target.value) || 50)}
                      style={{ width: '100%', padding: '0.4rem', background: '#0a0a0a', border: '1px solid #333', borderRadius: '0.4rem', color: '#fff', fontSize: '0.85rem' }} />
                  </div>
                </div>
              </div>

              {/* Gestión de Saldo */}
              <div style={{ marginBottom: '1.25rem', padding: '1rem', background: '#1a1a1a', borderRadius: '1rem' }}>
                <h4 style={{ color: '#ccff00', margin: '0 0 0.5rem', fontSize: '0.9rem' }}>💸 Saldo Manual</h4>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input type="number" id="manualAmount" placeholder="Monto USD" style={{ flex: 1, padding: '0.4rem', background: '#0a0a0a', border: '1px solid #333', borderRadius: '0.4rem', color: '#fff', fontSize: '0.85rem' }} />
                  <input type="text" id="manualDesc" placeholder="Motivo" style={{ flex: 1, padding: '0.4rem', background: '#0a0a0a', border: '1px solid #333', borderRadius: '0.4rem', color: '#fff', fontSize: '0.85rem' }} />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => {
                    const amt = document.getElementById('manualAmount')?.value;
                    const desc = document.getElementById('manualDesc')?.value;
                    if (amt) handleAddWalletBalance(editingUser._id, amt, desc);
                  }} style={{ flex: 1, padding: '0.4rem', background: '#00ff88', border: 'none', borderRadius: '0.4rem', color: '#000', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem' }}>
                    ➕ Añadir
                  </button>
                  <button onClick={() => {
                    const amt = document.getElementById('manualAmount')?.value;
                    const desc = document.getElementById('manualDesc')?.value;
                    if (amt) handleRemoveWalletBalance(editingUser._id, amt, desc);
                  }} style={{ flex: 1, padding: '0.4rem', background: '#ff0033', border: 'none', borderRadius: '0.4rem', color: '#fff', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem' }}>
                    ➖ Remover
                  </button>
                </div>
                <div style={{ marginTop: '0.4rem', fontSize: '0.7rem', color: '#9ca3af' }}>Saldo: ${editingUser.walletBalance || 0}</div>
              </div>

              {/* Botones */}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={() => setShowEditModal(false)} style={{ flex: 1, padding: '0.6rem', background: '#1a1a1a', border: '1px solid #333', borderRadius: '0.5rem', color: '#9ca3af', cursor: 'pointer', fontSize: '0.85rem' }}>
                  Cancelar
                </button>
                <button onClick={handleSavePermissions} style={{ flex: 1, padding: '0.6rem', background: 'linear-gradient(135deg, #00ffff, #0066ff)', border: 'none', borderRadius: '0.5rem', color: '#000', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem' }}>
                  💾 Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .loader {
          width: 40px; height: 40px;
          border: 3px solid #333;
          border-top-color: #00ffff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        input[type="checkbox"] {
          accent-color: #00ffff;
          width: 16px; height: 16px;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}

export default AdminPanel;